import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiCheckCircle } from 'react-icons/fi';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('orderId');
  const paymentPending = searchParams.get('payment') === 'pending';
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <div className="bg-success/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <FiCheckCircle className="w-12 h-12 text-success" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tighter text-primary-main mb-4" data-testid="success-heading">
          Order Placed Successfully!
        </h1>
        {paymentPending ? (
          <p className="text-lg text-text-secondary mb-8">
            Thank you for your order! Please complete payment to process your order.
          </p>
        ) : (
          <p className="text-lg text-text-secondary mb-8">
            Thank you for your order. Your research peptides will be shipped shortly.
          </p>
        )}

        {paymentPending && (
          <div className="bg-accent-subtle border border-primary-main rounded-lg p-8 mb-8 text-left">
            <h2 className="text-xl font-heading font-semibold text-primary-main mb-4">Payment Instructions</h2>
            <div className="space-y-4 text-sm">
              <p className="text-text-secondary">
                Please transfer <strong className="text-primary-main font-mono text-lg">${order?.total.toFixed(2)} AUD</strong> to the following account:
              </p>
              <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-text-muted">Bank Name:</span>
                  <span className="font-medium text-text-primary">Commonwealth Bank</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Account Name:</span>
                  <span className="font-medium text-text-primary">Precision Peptides AU</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">BSB:</span>
                  <span className="font-mono font-medium text-text-primary">062-000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Account Number:</span>
                  <span className="font-mono font-medium text-text-primary">1234 5678</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-text-muted">Reference:</span>
                  <span className="font-mono font-medium text-text-primary text-right">{order?.id.substring(0, 8).toUpperCase()}</span>
                </div>
              </div>
              <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
                <p className="text-xs text-text-secondary">
                  <strong className="text-warning">Important:</strong> Please include your order reference <strong className="font-mono">{order?.id.substring(0, 8).toUpperCase()}</strong> in the transfer description so we can match your payment.
                </p>
              </div>
              <p className="text-text-secondary">
                Once payment is confirmed, we'll process and ship your order within 1-2 business days. You'll receive a confirmation email with tracking information.
              </p>
            </div>
          </div>
        )}

        {order && (
          <div className="bg-background-subtle border border-slate-200 rounded-lg p-8 mb-8 text-left">
            <h2 className="text-xl font-heading font-semibold text-primary-main mb-4">Order Details</h2>
            <div className="space-y-2 text-sm mb-6">
              <div className="flex justify-between">
                <span className="text-text-secondary">Order ID:</span>
                <span className="font-mono text-text-primary" data-testid="order-id">{order.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Customer:</span>
                <span className="text-text-primary">{order.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Email:</span>
                <span className="text-text-primary">{order.customer_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Total:</span>
                <span className="font-mono font-semibold text-text-primary" data-testid="order-total">${order.total.toFixed(2)} AUD</span>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-heading font-semibold text-primary-main mb-3">Items Ordered</h3>
              <div className="space-y-2">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-text-secondary">
                      {item.product_name} x {item.quantity}
                    </span>
                    <span className="font-mono text-text-primary">${(item.price * item.quantity).toFixed(2)} AUD</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="bg-slate-100 border border-slate-200 rounded-lg p-6 mb-8">
          <p className="text-sm text-text-secondary">
            {paymentPending 
              ? "Payment instructions have been sent to your email. Please complete payment to process your order."
              : "A confirmation has been displayed on screen. Your order will be processed and shipped within 1-2 business days."
            }
          </p>
        </div>

        <button
          onClick={() => navigate('/shop')}
          className="btn-primary"
          data-testid="continue-shopping-button"
        >
          Continue Shopping
        </button>
      </div>
    </div>
  );
};

export default OrderSuccess;
